import { Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { StoragePrefix } from '@core/constant';
import { PwaInstallService } from '@core/services/pwa-install.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { StorageService } from '../utils';
import { filter } from 'rxjs';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, TranslateModule],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App {
  private _storage = inject(StorageService);
  private _translation = inject(TranslateService);
  private _pwaInstall = inject(PwaInstallService);
  private _router = inject(Router);

  private currentUrl = signal(this._router.url);
  private dismissedInstallPopup = signal(false);
  private dismissedIosInstallPopup = signal(false);
  showInstalledPopup = signal(false);
  showInstallPopup = computed(() =>
    this._pwaInstall.canInstall()
    && !this.isInstallAppPage()
    && !this._pwaInstall.isIosDevice()
    && !this._pwaInstall.isStandaloneMode()
    && !this.dismissedInstallPopup()
  );
  showIosInstallPopup = computed(() =>
    this._pwaInstall.isIosDevice()
    && !this._pwaInstall.isStandaloneMode()
    && !this.isInstallAppPage()
    && !this.dismissedIosInstallPopup()
  );

  constructor() {
    this.setLanguage()
    this._router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntilDestroyed()
    ).subscribe((event) => {
      this.currentUrl.set(event.urlAfterRedirects);
    });
  }

  setLanguage() {
    const lang = this._storage.get(StoragePrefix.LANGUAGE) || 'en';

    this._translation.use(lang);
    document.dir = lang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;

    if (!this._storage.get(StoragePrefix.LANGUAGE)) {
      this._storage.set(StoragePrefix.LANGUAGE, lang);
    }
  }

  async installPwa() {
    const outcome = await this._pwaInstall.install();
    this.dismissedInstallPopup.set(true);

    if (outcome === 'accepted') {
      this.showInstalledPopup.set(true);
    }
  }

  openInstalledApp() {
    this.showInstalledPopup.set(false);
    window.location.assign(window.location.origin);
  }

  closePopup() {
    this.dismissedInstallPopup.set(true);
    this.dismissedIosInstallPopup.set(true);
    this.showInstalledPopup.set(false);
  }

  private isInstallAppPage(): boolean {
    const currentPath = this.currentUrl().split('?')[0].split('#')[0];
    return currentPath === '/install-app';
  }
}
